/**
 * Custom Chart  component
 *
 * @package ext-components-pro
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React from "react";

import { ThemeProvider, PartialTheme } from '@fluentui/react';

import { createSubScope, defineElementaryComponent, Type } from "@adapptio/lib-blueprint";

import {
	Container,
	ContainerProps,
	isEventEnabled,
	TViewComponentDefinition,
	TComponentReact,
} from "@adapptio/lib-components";
import { termsEditor } from "../../terms";
import { BaseEvents } from "../../base/BaseEvents";

interface ComponentFluentTheme_State {}

const appTheme: PartialTheme = {
	palette: {
		themePrimary: 'red'
	}
};

/**
 * Fluent Theme props
 */
const ComponentFluentTheme_Props = {
	...ContainerProps
};

const ComponentFluentTheme_Events = {
	...BaseEvents
};

const ComponentFluentTheme_Definition = defineElementaryComponent<
	typeof ComponentFluentTheme_Props,
	ComponentFluentTheme_State,
	typeof ComponentFluentTheme_Events
>({
	...termsEditor.components.fluentUi.component,

	name: "fluentUi",

	category: "fluentUi",

	icon: "mdi/code-braces",

	order: 1000,

	container: true,

	props: ComponentFluentTheme_Props,

	events: ComponentFluentTheme_Events,

	resolve: (_spec, state) => {
		return state || {};
	},

	getScopeData: (_spec, state) => {
		return {};
	},

	getScopeType: () => {
		return Type.Object({
			props: {}
		});
	}
});

const ComponentFluentTheme_React: TComponentReact<typeof ComponentFluentTheme_Definition> = ({
	props,
	componentInstance,
	reactComponentClassList
}) => {

	const _clickHandler = React.useMemo(() => {
		if (isEventEnabled(componentInstance.eventEnabled.click, componentInstance.componentMode)) {
			return () => componentInstance.eventTriggers.click((parentScope) => createSubScope(parentScope));
		}

		return;
	}, [ componentInstance.componentMode, componentInstance.eventEnabled.click, componentInstance.eventTriggers.click ]);


	return (
		<ThemeProvider theme={appTheme}>
			<Container
				{...props}
				componentPath={componentInstance.safePath}
				componentMode={componentInstance.componentMode}
				classList={reactComponentClassList}
				contentModelNode={componentInstance.modelNode?.props.props.content as unknown as any}
				onClick={_clickHandler}
			/>
		</ThemeProvider>
	);
};

export const ComponentFluentTheme: TViewComponentDefinition<typeof ComponentFluentTheme_Definition> = {
	...ComponentFluentTheme_Definition,
	reactComponent: ComponentFluentTheme_React
};
